import { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

/**
 * Hook for fetching event-related data
 */
export const useEventData = (eventId) => {
  const [eventDetails, setEventDetails] = useState(null);
  const [topics, setTopics] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Specific topic group IDs to filter for LOUNGES events
  const ALLOWED_TOPIC_GROUP_IDS = [
    'b1f2ca84-0f9a-4d08-9b26-fd652a3ac5fc',
    '93ffdc86-e278-4a68-88b8-2d81a28cde65'
  ];

  /**
   * Fetch event details
   */
  const fetchEventDetails = useCallback(async (eventIdParam) => {
    const idToUse = eventIdParam || eventId;
    if (!idToUse) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query GetEvent($id: ID!) {
            getEvent(id: $id) {
              id
              name {
                language
                text
              }
              type
              boothBooking
              status
              date
              startDate
              endDate
              startTime
              endTime
              lectureRooms
              lectureRegistrationStartDate
              lectureRegistrationEndDate
              lectureEditEndDate
              lectureDocumentsEndDate
              lecturePresentationEndDate
              lectureRecordingCorrectionEndDate
              availableLectureTypes
              location {
                name
                address {
                  street
                  streetNumber
                  city
                  zip
                  countryCode
                }
                coordinates {
                  lat
                  lng
                }
              }
              industry {
                id
                name {
                  language
                  text
                }
              }
              suggestedInvitationCode
              exhibitorLectures
            }
          }
        `,
        variables: { id: idToUse }
      });
      
      const event = response.data.getEvent;
      setEventDetails(event);
      return event;
    } catch (error) {
      console.error('Error fetching event details:', error);
      setError(error.message || 'Failed to fetch event details');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  /**
   * Fetch all topics with their groups
   */
  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query ListTopicGroups(
            $filter: ModelTopicGroupFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
              items {
                id
                color
                sortKey
                name {
                  language
                  text
                }
                topics {
                  items {
                    id
                    name {
                      language
                      text
                    }
                    color
                    sortKey
                    topicGroupTopicsId
                  }
                }
              }
              nextToken
            }
          }
        `,
        variables: {
          limit: 1000
        }
      });
      
      const allTopicGroups = response.data.listTopicGroups.items || [];
      
      // Filter only the allowed topic groups if needed
      const filteredTopicGroups = allTopicGroups
        .filter(group => ALLOWED_TOPIC_GROUP_IDS.includes(group.id))
        .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));
      
      setTopicGroups(filteredTopicGroups);
      
      // Flatten topics from all groups
      const topicsFlat = filteredTopicGroups
        .map((topicGroup) => topicGroup.topics.items)
        .flat()
        .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));
      
      setTopics(topicsFlat);
      
      return { topicGroups: filteredTopicGroups, topics: topicsFlat };
    } catch (error) {
      console.error('Error fetching topics:', error);
      setError(error.message || 'Failed to fetch topics');
      return { topicGroups: [], topics: [] };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch topics for a specific event (may have event-specific filtering in the future)
   */
  const fetchEventTopics = useCallback(async (eventIdParam) => {
    // For now, just fetch all topics
    // In the future, this could filter based on event-specific configuration
    return fetchTopics();
  }, [fetchTopics]);

  /**
   * Get topic by ID
   */
  const getTopicById = useCallback((topicId) => {
    return topics.find(topic => topic.id === topicId);
  }, [topics]);

  /**
   * Get topics by IDs
   */
  const getTopicsByIds = useCallback((topicIds) => {
    if (!topicIds || !Array.isArray(topicIds)) return [];
    return topics.filter(topic => topicIds.includes(topic.id));
  }, [topics]);

  /**
   * Get topic group by ID
   */
  const getTopicGroupById = useCallback((groupId) => {
    return topicGroups.find(group => group.id === groupId);
  }, [topicGroups]);

  /**
   * Search topics by name
   */
  const searchTopics = useCallback((searchTerm, language = 'de') => {
    if (!searchTerm) return topics;
    
    const searchLower = searchTerm.toLowerCase();
    return topics.filter((topic) => {
      const topicName = topic.name?.find(n => n.language === language)?.text || '';
      return topicName.toLowerCase().includes(searchLower);
    });
  }, [topics]);

  /**
   * Get available lecture types for the event
   */
  const getAvailableLectureTypes = useCallback(() => {
    if (!eventDetails?.availableLectureTypes) {
      // Default lecture types if not specified
      return ['technical', 'marketing', 'educational', 'vip'];
    }
    
    try {
      // availableLectureTypes might be stored as JSON string
      if (typeof eventDetails.availableLectureTypes === 'string') {
        return JSON.parse(eventDetails.availableLectureTypes);
      }
      return eventDetails.availableLectureTypes;
    } catch (error) {
      console.error('Error parsing available lecture types:', error);
      return ['technical', 'marketing', 'educational', 'vip'];
    }
  }, [eventDetails]);

  /**
   * Check if a lecture type is available for the event
   */
  const isLectureTypeAvailable = useCallback((lectureType) => {
    const availableTypes = getAvailableLectureTypes();
    return availableTypes.includes(lectureType);
  }, [getAvailableLectureTypes]);

  /**
   * Auto-fetch event details when eventId changes
   */
  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId, fetchEventDetails]);

  return {
    eventDetails,
    topics,
    topicGroups,
    isLoading,
    error,
    fetchEventDetails,
    fetchTopics,
    fetchEventTopics,
    getTopicById,
    getTopicsByIds,
    getTopicGroupById,
    searchTopics,
    getAvailableLectureTypes,
    isLectureTypeAvailable
  };
};